import { Injectable } from '@angular/core';
import { Capacitor, PluginListenerHandle, registerPlugin } from '@capacitor/core';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './localStorage.service';
import { getUserDefaultsPlugin } from './native-storage.plugin';

// Define the plugin interface
export interface PusherBackgroundPlugin {
  /**
   * Démarre l'écoute Pusher native.
   *
   * Retourne `{ started: boolean }`. Sur Android, `started=false` signifie que
   * `startForegroundService` a échoué (typiquement Android 12+ avec app en
   * background sans exemption). Dans ce cas, le service N'écoute PAS et le
   * JS doit prendre le relais pour le son (cf `pusher.service.ts:sendVibrate`).
   *
   * Sur iOS, le retour est vide aujourd'hui (compat ascendante).
   */
  startListening(): Promise<{ started?: boolean }>;
  stopListening(): Promise<void>;
  /** Arrête la sonnerie urgente loopée (acceptation / expiration / annulation demande). */
  stopUrgentSound(): Promise<void>;
  setConfig(options: {
    pusherKey: string;
    pusherCluster: string;
    pusherAuthEndpoint: string;
    authToken: string;
    userRole: string;
    userId: string;
  }): Promise<void>;
  isListening(): Promise<{ isListening: boolean }>;
  /**
   * Retourne l'état courant `nativeReady` du natif. Sert à synchroniser le flag
   * JS au démarrage : le listener `addListener('nativeReady', ...)` peut être
   * registré APRÈS que le natif a déjà émis `ready=true` (race condition au
   * boot), ce qui laisse le flag JS à false et provoque un fallback son JS en
   * parallèle du natif → DOUBLE-SON.
   */
  getNativeReadyStatus(): Promise<{ ready: boolean }>;
  /**
   * Démarre la Live Activity iOS en mode `idle` (en attente de demandes).
   * À appeler quand le prestataire passe en cycle ON, en FOREGROUND.
   *
   * Stratégie idle+update : iOS refuse `Activity.request()` en background
   * (ActivityAuthorizationError.visibility). On crée donc la LA en foreground
   * via cette méthode, puis les pushs Pusher arrivant en background font
   * `activity.update()` (autorisé) pour passer en mode newDemande.
   *
   * No-op sur Android et iOS < 16.2.
   */
  startIdleLiveActivity(): Promise<void>;
  /**
   * Termine toutes les Live Activities iOS en cours. À appeler au logout
   * ou au cycle OFF du prestataire. No-op sur Android et iOS < 16.2.
   */
  endLiveActivity(): Promise<void>;
  /**
   * Termine la Live Activity SI elle affiche cette demande. Variante guardée
   * de `endLiveActivity()` qui évite de tuer une LA arrivée entre temps
   * (race accept ↔ nouveau push newDemande). Idempotent si pas de match.
   */
  endLiveActivityForDemande(options: { demandeId: number }): Promise<void>;
  /**
   * Reset la Live Activity en mode `idle` pour cette demande. À appeler
   * après acceptation ou refus d'une demande côté app (le prestataire
   * reste en service donc on ne ferme pas, on remet en attente).
   *
   * Garde anti-event-en-retard côté natif : reset UNIQUEMENT si la LA
   * affiche actuellement cette demande. No-op sinon.
   */
  resetLiveActivityIdle(options: { demandeId: number }): Promise<void>;
  /**
   * Retourne l'état des Live Activities iOS (support, activation utilisateur, push fréquents).
   */
  getLiveActivityStatus(): Promise<{
    iOSSupported: boolean;
    enabled: boolean;
    frequentPushesEnabled: boolean;
  }>;
  /**
   * Récupère et CONSOMME le dernier push FCM cliqué en cold start. Si l'app
   * a été démarrée par un clic FCM Android avant que le listener `pushClicked`
   * soit enregistré, MainActivity a stocké le payload natif ; cette méthode
   * permet au JS de le récupérer et faire le routing au tout début du démarrage.
   *
   * Retourne `{ hasPending: false }` si aucun clic en attente.
   */
  getPendingPushClick(): Promise<{ hasPending: boolean; data?: Record<string, string> }>;
  setPendingPushClick(options: { data: Record<string, string> }): Promise<void>;
  /**
   * Retourne le FCM token résolu par Firebase Messaging (iOS uniquement).
   */
  getFcmToken(): Promise<{ token: string }>;
  /**
   * Supprime le token Firebase Messaging local puis en génère un nouveau
   * avec le GoogleService-Info.plist embarqué dans le build courant.
   */
  refreshFcmToken(): Promise<{ token: string }>;
  /**
   * Émis par le natif pour signaler le changement d'état de readiness :
   * - `ready=true` : tous les channels attendus ont reçu `pusher:subscription_succeeded`,
   *   le natif est connecté ET abonné → il jouera le son via triggerNotificationEffects.
   * - `ready=false` : le WebSocket est tombé (close/failure) ou `stopListening`
   *   a été appelé → le JS doit reprendre le fallback son.
   *
   * Avant ce mécanisme, JS skippait le son dès `startListening()` (alors que le
   * natif n'était pas encore abonné) et continuait à skipper après une perte de
   * connexion → silence total. Régression GPT-5.5 review #5.
   */
  addListener(
    eventName: 'nativeReady',
    listenerFunc: (data: { ready: boolean }) => void
  ): Promise<PluginListenerHandle>;
  /**
   * Émis par MyFirebaseMessagingService quand un FCM push arrive
   * (équivalent du listener Capacitor `pushNotificationReceived`).
   *
   * `data` contient les keys du payload FCM (event_id, type_notification,
   * demande_id, etc.) sous forme de strings. À utiliser pour réagir à un
   * push reçu en foreground.
   */
  addListener(
    eventName: 'pushReceived',
    listenerFunc: (data: Record<string, string>) => void
  ): Promise<PluginListenerHandle>;
  /**
   * Émis par MainActivity quand l'utilisateur clique sur la bannière FCM
   * (équivalent du listener Capacitor `pushNotificationActionPerformed`).
   *
   * `data` contient les keys du payload FCM. À utiliser pour router le user
   * vers la page liée (cf. push-notification.service.ts).
   */
  addListener(
    eventName: 'pushClicked',
    listenerFunc: (data: Record<string, string>) => void
  ): Promise<PluginListenerHandle>;
  /**
   * Émis par le manager Pusher natif iOS quand il reçoit un event applicatif.
   * Sert de filet UI si le natif joue le son mais que Echo JS n'a pas reçu
   * l'event.
   */
  addListener(
    eventName: 'pusherEvent',
    listenerFunc: (data: { channel?: string; event?: string; data?: Record<string, any> }) => void
  ): Promise<PluginListenerHandle>;
  /**
   * Émis quand le natif a traité un event sonore (allowlist + dédup).
   * Synchronise le cache dédup JS pour éviter double son/UI via Echo en reconnexion.
   */
  addListener(
    eventName: 'eventProcessed',
    listenerFunc: (data: { data?: Record<string, any> }) => void
  ): Promise<PluginListenerHandle>;
}

// Register the native plugin
// IMPORTANT: Capacitor automatically adds "Plugin" suffix to the name
// So 'PusherBackground' maps to PusherBackgroundPlugin class
export const PusherBackground = registerPlugin<PusherBackgroundPlugin>('PusherBackground');

// Debug: Log plugin registration
if (!environment.production && typeof console !== 'undefined') {
  console.log('[PusherBackground] Plugin registered:', PusherBackground);
  console.log('[PusherBackground] Platform:', Capacitor.getPlatform());
  console.log('[PusherBackground] Plugin available:', !!PusherBackground);
}

@Injectable({
  providedIn: 'root',
})
export class PusherBackgroundService {
  private isListening = false;

  /**
   * Vrai si `startForegroundService` (Android) ou `startListening` (iOS) n'a pas
   * échoué. C'est une condition NÉCESSAIRE mais PAS SUFFISANTE pour que le natif
   * joue le son : il peut avoir démarré le process sans avoir encore complété la
   * connexion WebSocket et l'abonnement aux channels. Cf `nativeReady` ci-dessous
   * pour la condition suffisante.
   */
  private nativeStarted = false;

  /**
   * Vrai UNIQUEMENT quand le natif est connecté ET abonné à TOUS les channels
   * attendus (rôle + discussion). Réinitialisé à false sur perte du WebSocket
   * (close/failure) ou stopListening. Tant que ce n'est pas vrai, le JS joue
   * le son en fallback (cf `pusher.service.ts:sendVibrate`).
   *
   * Architecture : le natif émet un event `nativeReady` avec `{ready: bool}` à
   * chaque transition. Le listener ci-dessous met simplement à jour le flag.
   */
  private nativeReady = false;
  private subscriptionListenerHandle: PluginListenerHandle | null = null;
  private nativePusherEventListenerHandle: PluginListenerHandle | null = null;
  private nativeEventProcessedListenerHandle: PluginListenerHandle | null = null;
  private nativePusherEventSubject = new Subject<Record<string, any>>();
  private nativeEventProcessedSubject = new Subject<Record<string, any>>();

  constructor(private localStorageService: LocalStorageService) {
    // Écouter dès le démarrage du service les transitions `nativeReady` émises
    // par le plugin natif (à chaque connexion/déconnexion/reconnexion).
    if (Capacitor.isNativePlatform()) {
      PusherBackground.addListener('nativeReady', (data) => {
        console.log('[PusherBackground] nativeReady event received:', data);
        this.nativeReady = !!data?.ready;
      })
        .then((handle) => {
          this.subscriptionListenerHandle = handle;
          void this.syncNativeReadyStatus();
        })
        .catch((err) => {
          console.warn('[PusherBackground] Could not register nativeReady listener:', err);
        });

      PusherBackground.addListener('pusherEvent', (event) => {
        const rawData = event?.data ?? event;
        const data = typeof rawData === 'string' ? this.parseNativePusherData(rawData) : { ...rawData };
        const eventType = data?.["type_event"] ?? data?.["event_type"] ?? event?.event;
        if (!environment.production) {
          console.log('[PusherBackground] native pusherEvent received:', this.summarizeRealtimeEvent(event, data));
        }
        if (data) {
          if (eventType && data["type_event"] == null) {
            data["type_event"] = eventType;
          }
          this.nativePusherEventSubject.next(data);
        }
      })
        .then((handle) => {
          this.nativePusherEventListenerHandle = handle;
        })
        .catch((err) => {
          console.warn('[PusherBackground] Could not register pusherEvent listener:', err);
        });

      PusherBackground.addListener('eventProcessed', (event) => {
        const rawData = event?.data ?? event;
        const data =
          typeof rawData === 'string'
            ? this.parseNativePusherData(rawData)
            : { ...(rawData as Record<string, any>) };
        if (data && Object.keys(data).length > 0) {
          this.nativeEventProcessedSubject.next(data);
        }
      })
        .then((handle) => {
          this.nativeEventProcessedListenerHandle = handle;
        })
        .catch((err) => {
          console.warn('[PusherBackground] Could not register eventProcessed listener:', err);
        });
    }
  }

  nativePusherEventObservable() {
    return this.nativePusherEventSubject.asObservable();
  }

  nativeEventProcessedObservable() {
    return this.nativeEventProcessedSubject.asObservable();
  }

  private parseNativePusherData(data: string): Record<string, any> {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.warn('[PusherBackground] Could not parse native pusherEvent data:', error);
      return {};
    }
  }

  private summarizeRealtimeEvent(
    event: { channel?: string; event?: string },
    data: Record<string, any>
  ): Record<string, any> {
    const demande = data?.["demande"];
    const demandeObject = demande && typeof demande === "object" ? demande : null;

    return {
      channel: event?.channel,
      event: event?.event,
      type_event: data?.["type_event"] ?? data?.["event_type"] ?? data?.["type_notification"],
      event_id: data?.["event_id"],
      demande_id:
        data?.["demande_id"] ??
        data?.["demand_id"] ??
        data?.["id_demande"] ??
        demandeObject?.["id"] ??
        (typeof demande === "number" || typeof demande === "string" ? demande : data?.["id"]),
      statut: data?.["statut"] ?? demandeObject?.["statut"],
    };
  }

  /**
   * Check if native background Pusher is available
   */
  isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Vrai si le service natif Pusher a démarré (process / foreground service OK).
   * NE garantit PAS que le natif est connecté et abonné — utilisez `isNativeReady`
   * pour ça.
   */
  isNativeStarted(): boolean {
    return this.nativeStarted;
  }

  /**
   * Vrai si le natif est connecté ET abonné à au moins un channel — donc capable
   * de recevoir les events applicatifs et de jouer le son.
   *
   * `pusher.service.ts:sendVibrate` skip le son JS UNIQUEMENT si
   * `nativeReady === true`. Sinon, fallback JS pour ne pas être silencieux.
   */
  isNativeReady(): boolean {
    return this.nativeReady;
  }

  /**
   * Resynchronise le flag JS avec l'état natif courant (bidirectionnel).
   * À appeler au boot et au retour foreground pour corriger les events manqués
   * pendant la suspension WebView iOS.
   */
  async syncNativeReadyStatus(): Promise<boolean> {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      const { ready } = await PusherBackground.getNativeReadyStatus();
      const synced = !!ready;
      if (synced !== this.nativeReady) {
        console.log('[PusherBackground] syncNativeReadyStatus:', {
          previous: this.nativeReady,
          synced,
        });
      }
      this.nativeReady = synced;
      return synced;
    } catch (err) {
      console.warn('[PusherBackground] syncNativeReadyStatus failed:', err);
      return this.nativeReady;
    }
  }

  /**
   * Démarre la Live Activity iOS en mode `idle`. À appeler quand le prestataire
   * passe en cycle ON / se met en service, **depuis un contexte foreground**
   * (sinon iOS refuse la création avec `ActivityAuthorizationError.visibility`).
   *
   * Une fois la LA démarrée en foreground, les pushs Pusher arrivant en
   * background pourront la mettre à jour en mode newDemande sans contrainte
   * de visibility.
   *
   * Idempotent (no-op si une LA tourne déjà). No-op sur Android et iOS < 16.2.
   */
  async startIdleLiveActivity(): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }
    try {
      await PusherBackground.startIdleLiveActivity();
      console.log('[PusherBackground] idle Live Activity start requested');
    } catch (err) {
      console.warn('[PusherBackground] startIdleLiveActivity failed:', err);
    }
  }

  /**
   * Termine toutes les Live Activities iOS. À appeler au logout / cycle OFF.
   * No-op sur Android et iOS < 16.2.
   */
  async endLiveActivity(): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }
    try {
      await PusherBackground.endLiveActivity();
      console.log('[PusherBackground] Live Activities ended');
    } catch (err) {
      console.warn('[PusherBackground] endLiveActivity failed:', err);
    }
  }

  /**
   * Termine la Live Activity iOS SI elle affiche cette demande. Variante
   * guardée à utiliser après accept/refuse pour ne pas tuer une LA arrivée
   * entre temps (race window). No-op sur Android et iOS < 16.2.
   */
  async endLiveActivityForDemande(demandeId: number): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }
    try {
      await PusherBackground.endLiveActivityForDemande({ demandeId });
      console.log(`[PusherBackground] LA end requested for demande=${demandeId}`);
    } catch (err) {
      console.warn('[PusherBackground] endLiveActivityForDemande failed:', err);
    }
  }

  /**
   * Reset la Live Activity iOS en mode `idle` pour la demande spécifiée.
   * À appeler après acceptation ou refus d'une demande — la session
   * prestataire continue donc on remet la LA en attente plutôt que de la
   * fermer. Garde anti-event-en-retard côté natif.
   *
   * No-op sur Android et iOS < 16.2.
   */
  async resetLiveActivityIdle(demandeId: number): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }
    try {
      await PusherBackground.resetLiveActivityIdle({ demandeId });
      console.log(`[PusherBackground] LA reset to idle (demande=${demandeId})`);
    } catch (err) {
      console.warn('[PusherBackground] resetLiveActivityIdle failed:', err);
    }
  }

  /**
   * Set configuration for native Pusher service
   */
  async setConfig(): Promise<void> {
    if (!this.isAvailable()) {
      console.warn('[PusherBackground] Native platform required');
      return;
    }

    try {
      const user = this.localStorageService.getUserData();
      if (!user || !user.client || !user.token) {
        console.warn('[PusherBackground] User not authenticated');
        return;
      }

      const pusherKey = environment.pusher.key;
      const pusherCluster = environment.pusher.cluster;
      const pusherAuthEndpoint = environment.pusher.authEndpoint;
      // Ensure token is a string (not boolean)
      const authToken = typeof user.token === 'string' ? user.token : '';
      const userRole = user.client.roles?.[0] || '';
      const userId = user.client.id?.toString() || '';

      if (!pusherKey || !authToken || authToken === '' || !userRole || !userId) {
        console.warn('[PusherBackground] Missing required configuration');
        return;
      }

      // Try to use the plugin first (works on Android, might work on iOS)
      try {
        await PusherBackground.setConfig({
          pusherKey,
          pusherCluster,
          pusherAuthEndpoint,
          authToken,
          userRole,
          userId,
        });

        console.log('[PusherBackground] Configuration set successfully via plugin');
      } catch (pluginError: any) {
        // If plugin doesn't work (UNIMPLEMENTED on iOS), use fallback method
        if (pluginError?.code === 'UNIMPLEMENTED' && Capacitor.getPlatform() === 'ios') {
          console.warn('[PusherBackground] ⚠️ Plugin not available, using fallback method (UserDefaults)');
          
          // Fallback: Write directly to UserDefaults using UserDefaultsPlugin
          // This writes directly to UserDefaults.standard with exact keys (no prefix)
          try {
            const UserDefaultsPlugin = getUserDefaultsPlugin();
            
            // Try to write to UserDefaults
            try {
              await UserDefaultsPlugin.set({ key: 'pusher_key', value: pusherKey });
              await UserDefaultsPlugin.set({ key: 'pusher_cluster', value: pusherCluster });
              await UserDefaultsPlugin.set({ key: 'pusher_auth_endpoint', value: pusherAuthEndpoint });
              await UserDefaultsPlugin.set({ key: 'auth_token', value: authToken }); // Same key as location (shared)
              await UserDefaultsPlugin.set({ key: 'user_role', value: userRole }); // Same key as location (shared)
              await UserDefaultsPlugin.set({ key: 'user_id', value: userId });
              
              console.log('[PusherBackground] ✅ Config written to UserDefaults (fallback method)');
            } catch (userDefaultsError: any) {
              // If UserDefaultsPlugin also fails, use SecureStoragePlugin
              if (userDefaultsError?.code === 'UNIMPLEMENTED') {
                console.warn('[PusherBackground] ⚠️ UserDefaultsPlugin also unavailable, using SecureStoragePlugin');
                throw userDefaultsError; // Will be caught by outer catch
              } else {
                throw userDefaultsError;
              }
            }
          } catch (fallbackError: any) {
            console.error('[PusherBackground] ❌ Error writing to UserDefaults:', fallbackError);
            // Try SecureStoragePlugin as last resort
            try {
              const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
              await SecureStoragePlugin.set({ key: 'pusher_key', value: pusherKey });
              await SecureStoragePlugin.set({ key: 'pusher_cluster', value: pusherCluster });
              await SecureStoragePlugin.set({ key: 'pusher_auth_endpoint', value: pusherAuthEndpoint });
              await SecureStoragePlugin.set({ key: 'auth_token', value: authToken });
              await SecureStoragePlugin.set({ key: 'user_role', value: userRole });
              await SecureStoragePlugin.set({ key: 'user_id', value: userId });
              console.log('[PusherBackground] ✅ Config written via SecureStoragePlugin (last resort)');
            } catch (secureStorageError) {
              console.error('[PusherBackground] ❌ All fallback methods failed:', secureStorageError);
              throw pluginError; // Re-throw original error
            }
          }
        } else {
          throw pluginError;
        }
      }
    } catch (error) {
      console.error('[PusherBackground] Error setting config:', error);
      throw error;
    }
  }

  /**
   * Start native background Pusher listening
   */
  async startListening(): Promise<void> {
    if (!this.isAvailable()) {
      console.warn('[PusherBackground] Native platform required');
      return;
    }

    // Re-entrance :
    // - déjà listening ET natif démarré → no-op (cas nominal).
    // - déjà listening MAIS natif PAS démarré (échec précédent ex: Android 12+
    //   background restrict) → on retente. Sans ça, on resterait coincé en
    //   "Already listening" alors que le natif n'écoute pas.
    if (this.isListening && this.nativeStarted) {
      console.log('[PusherBackground] Already listening (native started)');
      return;
    }
    if (this.isListening && !this.nativeStarted) {
      console.log('[PusherBackground] Listening flag set but native NOT started → retrying startForegroundService');
    }

    // Reset nativeReady : on attend la confirmation `subscriptionSucceeded` du natif.
    // Tant qu'elle n'arrive pas, le JS jouera le son en fallback (cf sendVibrate).
    this.nativeReady = false;

    try {
      // Set config first
      await this.setConfig();

      // Try to use the plugin first
      try {
        const result = await PusherBackground.startListening();
        // Côté Android, le plugin retourne `started=false` si startForegroundService
        // a échoué (Android 12+ background restrict). On synchronise les deux flags
        // pour que la prochaine invocation puisse retenter (cf. condition de re-entrance
        // ci-dessus). Sans ça, isListening=true bloquerait le retry alors que le
        // natif n'écoute pas → silence total, régression GPT-5.5.
        // Côté iOS aujourd'hui : pas de `started` dans le retour → on suppose true.
        const started = result?.started !== false;
        this.nativeStarted = started;
        this.isListening = started;
        if (!started) {
          console.warn('[PusherBackground] ⚠️ Native service did NOT start (likely Android 12+ background restrict). isListening kept false to allow retry on next call. JS prend le relais (fallback sound) en attendant.');
        } else {
          console.log('[PusherBackground] Started listening via plugin (waiting for subscriptionSucceeded to mark nativeReady)');
        }
      } catch (pluginError: any) {
        // If plugin doesn't work (UNIMPLEMENTED on iOS), use fallback method
        if (pluginError?.code === 'UNIMPLEMENTED' && Capacitor.getPlatform() === 'ios') {
          console.warn('[PusherBackground] ⚠️ Plugin not available, using fallback method (UserDefaults)');
          
          // Fallback: Write command to UserDefaults using UserDefaultsPlugin
          try {
            const UserDefaultsPlugin = getUserDefaultsPlugin();
            try {
              await UserDefaultsPlugin.set({ key: 'native_pusher_command', value: 'start' });
              await new Promise(resolve => setTimeout(resolve, 500));
              this.isListening = true;
              this.nativeStarted = true;
              console.log('[PusherBackground] ✅ Start command sent to UserDefaults (fallback)');
            } catch (userDefaultsError: any) {
              if (userDefaultsError?.code === 'UNIMPLEMENTED') {
                console.warn('[PusherBackground] ⚠️ UserDefaultsPlugin unavailable, using SecureStoragePlugin');
                throw userDefaultsError; // Will be caught by outer catch
              } else {
                throw userDefaultsError;
              }
            }
          } catch (fallbackError: any) {
            console.error('[PusherBackground] ❌ Error writing command to UserDefaults:', fallbackError);
            // Try SecureStoragePlugin as last resort
            try {
              const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
              await SecureStoragePlugin.set({ key: 'native_pusher_command', value: 'start' });
              await new Promise(resolve => setTimeout(resolve, 500));
              this.isListening = true;
              this.nativeStarted = true;
              console.log('[PusherBackground] ✅ Start command sent via SecureStoragePlugin (last resort)');
            } catch (secureStorageError) {
              console.error('[PusherBackground] ❌ All fallback methods failed:', secureStorageError);
              this.nativeStarted = false;
              throw pluginError; // Re-throw original error
            }
          }
        } else {
          throw pluginError;
        }
      }
    } catch (error) {
      console.error('[PusherBackground] Error starting listening:', error);
      this.nativeStarted = false;
      this.nativeReady = false;
      throw error;
    }
  }

  /**
   * Stop native background Pusher listening
   */
  async stopListening(): Promise<void> {
    if (!this.isAvailable()) {
      return;
    }

    // ⚠️ NE PAS early-return sur `!this.isListening` : après un warm restart
    // de la WebView (ex: window.location.reload() au logout), le service
    // Angular est ré-instancié avec `isListening=false` mais le natif
    // singleton (PusherBackgroundManager.shared / PusherBackgroundService)
    // peut encore être en train d'écouter. Sans appel `stopListening()`
    // explicite, le natif continue de tourner et de jouer le son après
    // logout/disconnect/endDay. Le natif est idempotent (cf
    // PusherBackgroundManager.stopListening qui no-op proprement si déjà
    // arrêté), donc safe d'appeler toujours.

    try {
      // Try to use the plugin first
      try {
        await PusherBackground.stopListening();
        this.isListening = false;
        this.nativeStarted = false;
        this.nativeReady = false;
        console.log('[PusherBackground] Stopped listening via plugin');
      } catch (pluginError: any) {
        // If plugin doesn't work (UNIMPLEMENTED on iOS), use fallback method
        if (pluginError?.code === 'UNIMPLEMENTED' && Capacitor.getPlatform() === 'ios') {
          console.warn('[PusherBackground] ⚠️ Plugin not available, using fallback method (UserDefaults)');

          // Fallback: Write command to UserDefaults
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
          await SecureStoragePlugin.set({ key: 'native_pusher_command', value: 'stop' });

          this.isListening = false;
          this.nativeStarted = false;
          this.nativeReady = false;
          console.log('[PusherBackground] ✅ Stop command written to storage (fallback method)');
        } else {
          throw pluginError;
        }
      }
    } catch (error) {
      console.error('[PusherBackground] Error stopping listening:', error);
      throw error;
    }
  }

  /**
   * Arrête la sonnerie urgente loopée côté natif (acceptation / expiration demande).
   * No-op si le plugin n'est pas disponible.
   */
  async stopUrgentSound(): Promise<void> {
    if (!this.isAvailable()) {
      return;
    }
    try {
      await PusherBackground.stopUrgentSound();
      console.log('[PusherBackground] stopUrgentSound: urgent alert stopped');
    } catch (err) {
      console.warn('[PusherBackground] stopUrgentSound failed (no-op):', err);
    }
  }

  /**
   * Check if currently listening
   */
  async isListeningActive(): Promise<boolean> {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      const result = await PusherBackground.isListening();
      this.isListening = result.isListening;
      return result.isListening;
    } catch (error) {
      console.error('[PusherBackground] Error checking listening status:', error);
      return false;
    }
  }
}
